import { Card, Col, DatePicker, Divider, Form, Input, Row } from 'antd'

import {
  HivScreeningPlace,
  HivTestResult,
  InvestigationTestTypeCategory,
  HivAnamnesisFormSection
} from 'src/api/requests'
import { Text } from 'src/components'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { FieldLabel } from '../FieldLabel'
import { EnumRadio, EnumSelect } from '../fields/EnumRadio'
import { HivIcd10Select, Icd10CodeText } from '../fields/HivIcd10Select'
import { HivTestTypeSelect } from '../fields/HivTestTypeSelect'
import { HivFormItem } from '../HivFormItem'
import { SystemRefreshButton } from '../SystemRefreshButton'

type SlotProps = {
  slot: 'firstScreening' | 'secondScreening' | 'confirmation'
  number: string
  category: InvestigationTestTypeCategory
}

/** 17–19 are optional since 2026-10-15 (only 43 is required). */
const TestSlot = ({ slot, number, category }: SlotProps) => {
  const { t } = useLocales()
  const path = (field: string) => ['form', 'testing', slot, field]

  return (
    <>
      <Text size="xl">{`${number}. ${t(`hiv_anamnesis.testing.${slot}`)}`}</Text>

      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('testTypeId')}
            number={`${number}.1`}
            label={t('hiv_anamnesis.testing.test_type')}
          >
            <HivTestTypeSelect category={category} />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('date')}
            number={`${number}.2`}
            label={t('hiv_anamnesis.testing.date')}
          >
            <DatePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('result')}
            number={`${number}.3`}
            label={t('hiv_anamnesis.testing.result')}
          >
            <EnumRadio
              values={Object.values(HivTestResult)}
              labelPrefix="hiv_anamnesis.test_result"
            />
          </HivFormItem>
        </Col>
      </Row>
    </>
  )
}

/** C. HIV testing and diagnosis (fields 15–20.2). */
export const TestingCard = () => {
  const { t } = useLocales()

  const path = (...parts: string[]) => ['form', 'testing', ...parts]
  const form = Form.useFormInstance()
  const diagnosisId = Form.useWatch(path('diagnosis', 'icd10Id'), form)

  return (
    <Card
      title={t('hiv_anamnesis.testing.title')}
      extra={
        <SystemRefreshButton sections={[HivAnamnesisFormSection.TESTING]} />
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('testingCode')}
            number="15"
            label={t('hiv_anamnesis.testing.testing_code')}
          >
            <Input maxLength={100} />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('additionalCode')}
            number="15.1"
            label={t('hiv_anamnesis.testing.additional_code')}
          >
            <Input maxLength={100} />
          </HivFormItem>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('screeningPlace')}
            number="16"
            label={t('hiv_anamnesis.testing.screening_place')}
          >
            <EnumSelect
              values={Object.values(HivScreeningPlace)}
              labelPrefix="hiv_anamnesis.screening_place"
            />
          </HivFormItem>
        </Col>
      </Row>

      <Divider />
      <TestSlot
        slot="firstScreening"
        number="17"
        category={InvestigationTestTypeCategory.A1}
      />

      <Divider />
      <TestSlot
        slot="secondScreening"
        number="18"
        category={InvestigationTestTypeCategory.A2}
      />

      <Divider />
      <TestSlot
        slot="confirmation"
        number="19"
        category={InvestigationTestTypeCategory.A3}
      />

      <Divider />
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <HivFormItem
            name={path('diagnosis', 'icd10Id')}
            number="20"
            label={t('hiv_anamnesis.testing.diagnosis')}
          >
            <HivIcd10Select withCode={false} />
          </HivFormItem>
        </Col>

        {/* 20.1 is the code of the diagnosis chosen in 20, not a separate answer. */}
        <Col xs={24} lg={4}>
          <Form.Item
            label={
              <FieldLabel
                number="20.1"
                label={t('hiv_anamnesis.testing.diagnosis_code')}
                field="testing.diagnosis.icd10Id"
              />
            }
          >
            <Icd10CodeText elementId={diagnosisId} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('diagnosis', 'date')}
            number="20.2"
            label={t('hiv_anamnesis.testing.diagnosis_date')}
          >
            <DatePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
              disabledDate={disabledFutureDate}
            />
          </HivFormItem>
        </Col>
      </Row>
    </Card>
  )
}
